import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Select from 'react-select';
import _ from 'lodash';

import { searchObjects } from '../utils/search';

const addressFilter = (filter, rows) =>
  filter.value
    ? searchObjects(rows, filter.value, [
        'address.address1',
        'address.city',
        'address.state',
        'address.country',
        'address.zipcode',
      ])
    : rows;

const baseFilter = (filter, rows) =>
  filter.value ? searchObjects(rows, filter.value, [filter.id]) : rows;

const baseTypeFilter = (filter, rows) =>
  filter.value ? searchObjects(rows, filter.value, [item => item[filter.id].code]) : rows;

const defaultFilter = (filter, rows) =>
  _.get(rows, '0._aggregated') ? rows : baseFilter(filter, rows);

const defaultSelectFilter = (filter, rows) =>
  _.get(rows, '0._aggregated') ? rows : baseFilter(filter, rows);

const defaultTypeFilter = (filter, rows) =>
  _.get(rows, '0._aggregated') ? rows : baseTypeFilter(filter, rows);

const defaultCountFilter = (filter, rows) => {
  if (_.get(rows, '0._aggregated')) {
    return rows;
  }

  if (!filter.value) {
    return rows;
  }

  return filter.value === 'has'
    ? _.filter(rows, row => row[filter.id] > 0)
    : _.filter(rows, row => row[filter.id] === 0);
};

const makeOptions = types =>
  _.map(types, type => {
    if (_.isObject(type)) {
      return {
        value: type.code || type.id,
        label: type.name || type.code,
      };
    }

    return {
      value: type,
      label: type,
    };
  });

const PlaceholderFilter = props => (
  <input
    type="text"
    placeholder={props.placeholder}
    value={props.filter ? props.filter.value : ''}
    onChange={event => props.onChange(event.target.value)}
  />
);

PlaceholderFilter.propTypes = {
  filter: PropTypes.shape({
    value: PropTypes.node,
  }),
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
};

PlaceholderFilter.defaultProps = {
  placeholder: 'Search...',
};

const makePlaceholderFilter = placeholder => props => (
  <PlaceholderFilter {...props} placeholder={placeholder} />
);

// Select Filters
const handleChange = (multi, onChange) =>
  multi ? values => onChange(_.map(values, val => val.value)) : val => onChange(val.value);

// In Table Select Filter
const SelectFilter = ({ filter, multi, name, onChange, small, types, ...props }) => (
  <Select
    className={classnames({ small })}
    clearable={false}
    multi={multi}
    name={name}
    onChange={handleChange(multi, onChange)}
    options={makeOptions([{ code: '', name: 'All' }, ...types])}
    searchable={false}
    value={filter ? filter.value : null}
    {...props}
  />
);

SelectFilter.propTypes = {
  filter: PropTypes.shape({
    value: PropTypes.node,
  }),
  multi: PropTypes.bool,
  name: PropTypes.string,
  small: PropTypes.bool,
  types: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onChange: PropTypes.func,
};

const makeSelectFilter = (name, types, small = false) => props => (
  <SelectFilter {...props} name={name} types={types} small={small} />
);

const makeAssignmentFilter = (name, types, small = false) => props => (
  <SelectFilter
    {...props}
    name={name}
    types={[{ code: 'unassigned', name: 'Unassigned' }, ...types]}
    small={small}
  />
);

const makeCountSelectFilter = (name, label, small = false) =>
  makeSelectFilter(
    name,
    [{ code: 'has', name: `Has ${label}` }, { code: 'not', name: `No ${label}` }],
    small,
  );

// External Table Select Filter
const ExternalSelectFilter = ({ multi, name, onChange, types, ...props }) => (
  <Select
    clearable={false}
    multi={multi}
    name={name}
    onChange={handleChange(multi, onChange)}
    options={makeOptions(types)}
    {...props}
  />
);

ExternalSelectFilter.propTypes = {
  multi: PropTypes.bool,
  name: PropTypes.string,
  types: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onChange: PropTypes.func,
};

export default {
  ExternalSelectFilter,

  addressFilter,
  defaultFilter,
  defaultSelectFilter,
  defaultTypeFilter,
  defaultCountFilter,
  makeAssignmentFilter,
  makeCountSelectFilter,
  makePlaceholderFilter,
  makeSelectFilter,
};
